/**
 * Utility functions for the e-commerce system
 * Common helper functions used across modules
 */

import { formatPrice as i18nFormatPrice } from './i18n.js';

/**
 * Escape HTML to prevent XSS attacks
 * @param {string} text - Text to escape
 * @returns {string} Escaped HTML
 */
export function escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text || '';
    return div.innerHTML;
}

/**
 * Format price with currency (now uses i18n system)
 * @param {number} price - Price to format
 * @returns {string} Formatted price string
 */
export function formatPrice(price) {
    return i18nFormatPrice(price);
}

/**
 * Debounce function to limit function calls
 * @param {Function} func - Function to debounce
 * @param {number} wait - Wait time in milliseconds
 * @returns {Function} Debounced function
 */
export function debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
        const later = () => {
            clearTimeout(timeout);
            func(...args);
        };
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
    };
}

/**
 * Get DOM element with caching
 * @param {string} selector - CSS selector
 * @param {Map} cache - Cache map for storing elements
 * @returns {Element|null} DOM element or null
 */
export function getElement(selector, cache = new Map()) {
    if (!cache.has(selector)) {
        cache.set(selector, document.querySelector(selector));
    }
    return cache.get(selector);
}

/**
 * Get all DOM elements matching selector
 * @param {string} selector - CSS selector
 * @returns {NodeList} NodeList of matching elements
 */
export function getAllElements(selector) {
    return document.querySelectorAll(selector);
}

/**
 * Dispatch custom event with cart update information
 * @param {Array} items - Cart items
 */
export function dispatchCartUpdatedEvent(items) {
    // Ensure items is an array
    const safeItems = Array.isArray(items) ? items : [];
    
    // Calculate total items with fallback for missing OrderCount
    const totalItems = safeItems.reduce((sum, item) => {
        const count = item.OrderCount || item.quantity || 0;
        return sum + (isNaN(count) ? 0 : count);
    }, 0);
    
    const event = new CustomEvent('cartUpdated', { 
        detail: { 
            items: safeItems, 
            totalItems: totalItems
        } 
    });
    document.dispatchEvent(event);
}

/**
 * Analyze cart to determine if it contains only gift cards, only products, or mixed
 * @param {Array} cartItems - Array of cart items
 * @returns {string} 'only-giftcards', 'only-products', or 'mixed'
 */
export function getCartType(cartItems) {
    if (!Array.isArray(cartItems) || cartItems.length === 0) {
        return 'only-products'; // Default to products for empty cart
    }
    
    const hasGiftCards = cartItems.some(item => item.isGiftCard === true);
    // Surcharge items (e.g., physical gift card fee) should not count as products
    const hasProducts = cartItems.some(item => !item.isGiftCard && item.Type !== 'Surcharge');
    
    if (hasGiftCards && !hasProducts) {
        return 'only-giftcards';
    } else if (!hasGiftCards && hasProducts) {
        return 'only-products';
    } else {
        return 'mixed';
    }
}
